import UpdateProfile from "../../components/module/account-setting/update-profile";
import ProfileView from "../../components/module/account-setting/profile-view";
import UpdatePassword from "../../components/module/account-setting/update-password";
import { useDispatch, useSelector } from "react-redux";
import { getErrorMessage } from "../../utils/get-error-message.utils";
import { LucideFilePenLine, ProiconsKey } from "../../assets/icon/iconify";
import { getInitials } from "../../utils/get-initials.utils";
import useDateGreeting from "../../hooks/common/use-date-greeting";
import AccountHeader from "../../components/module/account-setting/account-header";
import { Spinner } from "../../assets/icon/svg";
import { selectAccountSettingMode, setAccountSettingMode } from "../../features/it-user-forms/user-forms-slice";
import { useCreateUser } from "../../hooks/actions/use-create-user";
import { useDynamicQuires } from "../../hooks/services/dynamic-queries/use-dynamic-quires";
import PasswordWarningBanner from "../../components/module/account-setting/password-warning-banner";
import { useAuthDataState } from "../../hooks/data-state/use-auth-data-state";

export default function AccountSettingsPage() {

    const dispatch = useDispatch();
    const { userId, isUserMonitoring, credentials } = useAuthDataState();
    const mode = useSelector(selectAccountSettingMode);
    
    const {
        userData,
        isLoadingUserData,
        isErrorUserData,
        errorUserData,
    } = useDynamicQuires({
        currentId: userId!,
    });

    const isChangePassword = userData?.mustChangePassword ?? false;
    const errorMessage = getErrorMessage(errorUserData);

    const { greeting, formattedDate } = useDateGreeting();

    const firstAndLastLetter = getInitials(
        userData?.firstname,
        userData?.lastname
    );

    const { handleIsChangePassword } = useCreateUser();

    const renderContent = () => {
        switch (mode) {
            case "updateProfile":
                return <UpdateProfile userData={userData} />;
            case "updatePassword":
                return <UpdatePassword />;
            default:
                return <ProfileView userData={userData} />;
        }
    };

    const showWarningPassword =
        isChangePassword &&
        ["view", "updateProfile"].includes(mode);

    return (
    <div className="flex h-full min-h-0 w-full flex-col overflow-hidden bg-[#f5f9fd] rounded-xl border border-gray-200/80">
        {/* <div className="mx-auto flex w-full max-w-[1800px] flex-col gap-2.5"> */}
            {/* ACCOUNT HEADER */}
            {userData && (
                <div className="sticky top-0 z-20 flex-shrink-0 border-b border-gray-200 bg-white shadow-sm">
                    <AccountHeader
                        firstAndLastLetter={firstAndLastLetter}
                        greeting={greeting}
                        userName={userData.firstname}
                        formattedDate={formattedDate}
                    />
                </div>
            )}

            {/* PASSWORD WARNING */}
            {showWarningPassword && (
                <div className="p-3">
                    <PasswordWarningBanner handleChangePassword={handleIsChangePassword} />
                </div>
            )}

            {/* MAIN ACCOUNT CARD */}
            <div className="min-h-0 flex-1 overflow-y-auto">
                <div className="w-full px-3 py-3">
                {/* CONTENT */}
                {isLoadingUserData ? (
                    <div className="flex min-h-[300px] items-center justify-center px-4 py-10">
                        <div className="flex flex-col items-center gap-2">
                            <Spinner color="#186cb2" size={28} />
                            <span className="text-xs font-medium text-gray-500">
                                Loading account information...
                            </span>
                        </div>
                    </div>
                ) : isErrorUserData ? (
                    <div className="flex min-h-[300px] items-center justify-center px-4 py-10">
                        <div className="flex max-w-sm flex-col items-center text-center">
                            <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-red-50 text-red-500">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="1.8"
                                    className="h-5 w-5"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M12 9v4m0 4h.01M10.3 3.84 2.73 17a2 2 0 0 0 1.73 3h15.08a2 2 0 0 0 1.73-3L13.7 3.84a2 2 0 0 0-3.4 0z"
                                    />
                                </svg>
                            </div>
                            <p className="text-xs font-medium text-gray-700">
                                Unable to load your account information.
                            </p>
                            <p className="mt-1 text-[11px] text-gray-400">
                                {errorMessage}
                            </p>
                        </div>
                    </div>
                ) : (
                    <>
                        {/* ACCOUNT CONTENT */}
                        <div className="">
                            {renderContent()}
                        </div>
                        {/* ACTIONS */}
                        {mode === "view" && (
                            <div className="flex flex-wrap justify-end gap-2 border-t border-gray-100 bg-gray-50/70 px-3 py-3 sm:px-4">
                                <button
                                    type="button"
                                    disabled={isUserMonitoring}
                                    onClick={() => {
                                        if(isUserMonitoring) return;
                                        dispatch(setAccountSettingMode("updateProfile"))
                                    }}
                                    className="disabled:cursor-not-allowed flex items-center justify-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-gray-700 shadow-sm transition hover:border-[#186cb2]/30 hover:bg-[#186cb2]/5 hover:text-[#186cb2] active:scale-[0.98]"
                                >
                                    <LucideFilePenLine height={15} width={15} />
                                    <span>Update Profile</span>
                                </button>
                                <button
                                    type="button"
                                    disabled={isUserMonitoring}
                                    onClick={() => {
                                        if(isUserMonitoring) return;
                                        dispatch(setAccountSettingMode("updatePassword"))
                                    }}
                                    className="disabled:cursor-not-allowed flex items-center justify-center gap-1.5 rounded-lg bg-[#186cb2] px-3 py-2 text-xs font-semibold text-white shadow-sm shadow-[#186cb2]/20 transition hover:bg-[#155d9b] active:scale-[0.98]"
                                >
                                    <ProiconsKey height={15} width={15} />
                                    <span>Change Password</span>
                                </button>
                            </div>
                        )}
                    </>
                )}
                </div>
            </div>
        </div>
    );
}